import { TourGuideStatusType } from '@/config';
import { CurrentUserModel, TourGuideStatus } from '@/__generated';
import { IAppUser } from '@/contexts/AppContext';

export enum APP_ROLE {
  Admin = 'Admin',
  SupervisorUser = 'SupervisorUser',
  RegularUser = 'RegularUser',
  ReferenceUser = 'ReferenceUser',
  Guide = 'Guide',
  Assign = 'Assign',
  GuideSupport = 'GuideSupport',
  SuperAdmin = 'SuperAdmin',
  SuperAccounting = 'SuperAccounting',
  ReferenceAccounting = 'ReferenceAccounting',
  RegularAccounting = 'RegularAccounting',
}
const USER_SELECTED_ROLE = 'USER_SELECTED_ROLE';

export const ROLES_ADMIN = [APP_ROLE.SuperAdmin, APP_ROLE.Admin];
export const ROLES_STAFF = [APP_ROLE.ReferenceUser, APP_ROLE.RegularUser, APP_ROLE.SupervisorUser, APP_ROLE.Assign];
export const SEISAN_ROLES = [
  APP_ROLE.Guide,
  APP_ROLE.GuideSupport,
  APP_ROLE.RegularAccounting,
  APP_ROLE.SuperAccounting,
  APP_ROLE.ReferenceAccounting,
];

export const saveSelectedRole = (role: string | string | undefined) => {
  if (role) {
    localStorage.setItem(USER_SELECTED_ROLE, role);
  } else {
    localStorage.removeItem(USER_SELECTED_ROLE);
  }
};
export const getSelectedRole = () => {
  return localStorage.getItem(USER_SELECTED_ROLE) || '';
};

export const getDefaultSelectedRole = originRoles => {
  const prevRoles = localStorage.getItem(USER_SELECTED_ROLE);
  if (prevRoles === APP_ROLE.GuideSupport && originRoles.includes(prevRoles)) {
    return [APP_ROLE.GuideSupport];
  }
  saveSelectedRole(undefined);
  return originRoles.filter(x => x !== APP_ROLE.GuideSupport);
};

export const setBodyClassWithRole = role => {
  document.body.classList.add(`role-${role || ''}`);
};
export const excludeAccountingRoles = (roles: string[]) => {
  return roles?.filter(x => ![APP_ROLE.SuperAccounting, APP_ROLE.ReferenceAccounting, APP_ROLE.RegularAccounting].includes(x as any)) || [];
};
export const isAdminOrStaff = (roles: string[]) => {
  return roles?.some(r => [...ROLES_ADMIN, ...ROLES_STAFF].includes(r as any));
};
export const isSupperAdmin = (role: string[] | null | undefined) => role?.includes(APP_ROLE.Admin) || role?.includes(APP_ROLE.SuperAdmin);
export const isGuide = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.Guide, APP_ROLE.GuideSupport].includes(r as any));
export const isGuideOnly = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.Guide].includes(r as any));
export const isGuideSupport = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.GuideSupport].includes(r as any));
export const isReferenceAccounting = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.ReferenceAccounting].includes(r as any));
export const isSuperAccounting = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.SuperAccounting].includes(r as any));
export const isRegularAccounting = (role: string[] | null | undefined) => role?.some(r => [APP_ROLE.RegularAccounting].includes(r as any));

export const isAccounting = (role: string[] | null | undefined) =>
  role?.some(r => [APP_ROLE.SuperAccounting, APP_ROLE.ReferenceAccounting, APP_ROLE.RegularAccounting].includes(r as any));

export const hasRoles = (roles: APP_ROLE[] | '*' | undefined, auth: CurrentUserModel | null) => {
  return roles === '*' || auth?.roleClaims?.some(x => roles?.includes(x as APP_ROLE));
};
export const hasPermissionUpdateTour = (auth: CurrentUserModel | null) => {
  return auth?.roleClaims?.some(x =>
    [APP_ROLE.SuperAdmin, APP_ROLE.Admin, APP_ROLE.Assign, APP_ROLE.SupervisorUser, APP_ROLE.Guide, APP_ROLE.GuideSupport].includes(x as any),
  );
};
export const hasPermissionUpdateGuideSeisan = (auth: IAppUser | null) => {
  return isGuide(auth?.roleClaims) || isAccounting(auth?.originRoleClaims);
};
export const hasPermissionCreateTour = (auth: CurrentUserModel | null) => {
  return auth?.roleClaims?.some(x => [APP_ROLE.SuperAdmin, APP_ROLE.Admin, APP_ROLE.Assign, APP_ROLE.SupervisorUser].includes(x as any));
};

export const hasPermissionEditNotification = (auth: CurrentUserModel | null) => {
  return auth?.roleClaims?.some(x => [APP_ROLE.SuperAdmin, APP_ROLE.Admin, APP_ROLE.Assign, APP_ROLE.SupervisorUser].includes(x as any));
};

export const guideHasBall = (status: TourGuideStatus) => {
  return [
    TourGuideStatusType.Assigning,
    TourGuideStatusType.Informing3MonthsPrior,
    TourGuideStatusType.Informing2MonthPrior,
    TourGuideStatusType.FinalInforming,
    TourGuideStatusType.AwaitingCancellation,
  ].includes(status);
};
export const staffHasBall = (status: TourGuideStatus) => {
  return [
    TourGuideStatusType.Unassigned,
    TourGuideStatusType.Checking,
    TourGuideStatusType.Assigned,
    TourGuideStatusType.Checked,
    TourGuideStatusType.Finalized,
    TourGuideStatusType.FinalChecking,
    TourGuideStatusType.Cancelled,
    TourGuideStatusType.Denied,
  ].includes(status);
};
